import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Crown, Phone, Mail, Scissors, HelpCircle } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Cost Cutter', path: '/cost-cutter', highlight: true, icon: Scissors },
    { name: 'Services', path: '/services' },
    { name: 'Industries', path: '/industries' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'IT Answers', path: '/answers', icon: HelpCircle },
    { name: 'Blog', path: '/blog' },
    { name: 'Ticket Support', path: '/support', red: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
      {/* Top Bar - Desktop Only */}
      <div className="bg-secondary text-secondary-foreground py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <a href="mailto:support@kingofit.ca" className="flex items-center gap-2 hover:text-primary transition-colors">
            <Mail className="w-4 h-4" />
            <span>support@kingofit.ca</span>
          </a>
          <span className="text-muted-foreground">
            IT Consulting in a 360° manner
          </span>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative">
              <Crown className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-primary group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-bold text-base sm:text-lg md:text-xl text-secondary leading-none">
                KING<span className="text-primary">of</span>IT
              </span>
              <span className="text-[9px] sm:text-[10px] text-muted-foreground hidden sm:block">IT Consulting</span>
            </div>
          </Link>

          {/* Phone Number - Centered on Mobile, visible on all screens */}
          <a 
            href="tel:6479513653" 
            className="flex items-center gap-1 sm:gap-2 hover:text-primary transition-colors lg:hidden"
          >
            <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span className="text-base sm:text-lg md:text-xl font-bold text-secondary">
              647-951-3653
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-1.5 ${
                  link.highlight
                    ? 'bg-gradient-gold text-primary-foreground shadow-gold font-semibold hover:opacity-90'
                    : link.red
                    ? 'font-bold text-red-500 hover:text-red-600 hover:bg-red-50'
                    : isActive(link.path)
                    ? 'bg-muted text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted font-medium'
                }`}
              >
                {link.icon && <link.icon className="w-4 h-4" />}
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button + Phone for Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <a 
              href="tel:6479513653" 
              className="flex items-center gap-1.5 hover:text-primary transition-colors"
            >
              <Phone className="w-4 h-4 text-primary" />
              <span className="text-base font-bold text-secondary whitespace-nowrap">
                647-951-3653
              </span>
            </a>
            <a 
              href="https://outlook.office.com/bookwithme/user/20aee090aecf4040bdf81b47ab3eb662@kingofit.ca/meetingtype/53RXj902RESlZcG0PgiU7w2?anonymous&ismsaljsauthenabled&ep=mcard"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                Book a Consultation
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm transition-all flex items-center gap-2 ${
                    link.highlight
                      ? 'bg-gradient-gold text-primary-foreground font-semibold'
                      : link.red
                      ? 'font-bold text-red-500'
                      : isActive(link.path)
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted font-medium'
                  }`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Contact Info */}
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <a href="tel:6479513653" className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-primary">
                  <Phone className="w-4 h-4" />
                  <span>647-951-3653</span>
                </a>
                <a href="mailto:support@kingofit.ca" className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-primary">
                  <Mail className="w-4 h-4" />
                  <span>support@kingofit.ca</span>
                </a>
              </div>
              
              <a 
                href="https://outlook.office.com/bookwithme/user/20aee090aecf4040bdf81b47ab3eb662@kingofit.ca/meetingtype/53RXj902RESlZcG0PgiU7w2?anonymous&ismsaljsauthenabled&ep=mcard"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                <Button className="mt-4 w-full bg-gradient-gold text-primary-foreground font-semibold shadow-gold">
                  Book a Consultation
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
